import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ButtonGroup } from "../../core/components/buttons/ButtonGroup";
import { InfoText } from "../../core/components/texts/InfoText";
import { getIsReleaseSession, getStaffFillDashboardOrigin } from "../../core/store/currentFormSlice";
import { useMainStore } from "../../core/store/mainStore";
import { useReturnAfterIdle } from "../../core/utils/useReturnAfterIdle";
import { handleLogout } from "../navigation/utils/handleLogout";
import { RELEASE_DONE_PATH, useEndReleaseSession } from "../navigation/utils/webAppSession";
import { OnboardingScreenContainer } from "./OnboardingScreenContainer";

const RETURN_TO_START_IDLE_MS = 60_000;

export const SuccessScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Set only by the link's last form (PF-6132): the session ends here, outside InAppLayout.
  const routeState = location.state as
    | { allFormsCompleted?: boolean; signedOut?: boolean; viaRelease?: boolean; dashboardOrigin?: string }
    | null;
  const allFormsCompleted = routeState?.allFormsCompleted === true;
  const endReleaseSession = useEndReleaseSession();
  // Read before the logout below clears the session keys, and kept in the route state for a later render of this
  // entry: a release session finishes on its own end screen (PF-6133).
  const [viaRelease] = useState(
    () => routeState?.viaRelease === true || getIsReleaseSession(useMainStore.getState()),
  );
  // The dashboard embedding a staff-fill session (PF-6402); the end screen posts its done signal there.
  const [dashboardOrigin] = useState(
    () => routeState?.dashboardOrigin ?? getStaffFillDashboardOrigin(useMainStore.getState()),
  );

  useEffect(() => {
    if (!allFormsCompleted || routeState?.signedOut) return;
    handleLogout();
    // Back and forward can return to this entry later; it must not end a session begun since.
    navigate(location.pathname, {
      replace: true,
      state: { allFormsCompleted: true, signedOut: true, viaRelease, dashboardOrigin },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextButton = {
    onClick: () => {
      navigateNext();
    },
    text: t("BUTTON_COMPLETE"),
  };

  const navigateNext = () => {
    if (allFormsCompleted) {
      navigate(viaRelease ? RELEASE_DONE_PATH : "/welcome", {
        replace: true,
        state: viaRelease && dashboardOrigin ? { dashboardOrigin } : undefined,
      });
      return;
    }
    // Only reached when opened by URL: both entries into this screen set the route state.
    if (viaRelease) {
      void endReleaseSession({ ended: false });
    } else {
      handleLogout();
      navigate("/welcome", { replace: true });
    }
  };

  const isSessionEnded = allFormsCompleted && routeState?.signedOut === true;
  useReturnAfterIdle(isSessionEnded ? RETURN_TO_START_IDLE_MS : false, navigateNext);

  return (
    <OnboardingScreenContainer hideBackButton>
      <InfoText headline={t("SUCCESS_SCREEN_END_TITLE")} text={[t("SUCCESS_SCREEN_END_DESCRIPTION")]} />
      <StyledButtonGroup progressButton={nextButton} />
    </OnboardingScreenContainer>
  );
};

const StyledButtonGroup = styled(ButtonGroup)`
  align-items: flex-end;
  width: 100%;
`;
